import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlParamReplicaId, urlProjectViews } from '../../Constants';
import { IAction, IAppReduxStore, SlicePropsWithData } from '../store';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectViewsState = {
    data: null,
    loading: false,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches the available document views for a given project database.
     * @param replicaId - ReplicaId for the target project database.
     * @returns Promise for the HTTP request.
     */
    getProjectViewConfig: async (replicaId: string): Promise<ViewDictionary> => {
        try {
            const url = urlProjectViews.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectViewsState>) => {
        /**
         * Reducers for the 'getProjectViewConfig' thunk.
         */

        builder.addCase(getProjectViewConfig.pending, (state: Draft<IProjectViewsState>) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getProjectViewConfig.fulfilled,
            (state: Draft<IProjectViewsState>, action: IAction<ViewDictionary>) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getProjectViewConfig.rejected,
            (state: Draft<IProjectViewsState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = null;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectViewConfig = createAsyncThunk('project/views', thunks.getProjectViewConfig);

/** Dictionary that contains configuration data on project views. */
export type ViewDictionary = { [id: string]: IProjectViewData };

/**
 * Interface that describes the state object for this redux slice.
 */
export type IProjectViewsState = SlicePropsWithData<ViewDictionary>;

/**
 * Interface that describes data returned from the backend for a project database view.
 */
export interface IProjectViewData {
    /** Full View Name. */
    name: string;

    /** Dictionary of translations for the view's display name. */
    translations: { [id: string]: string };

    /** MD5 hash of the view name. */
    id: string;

    /** Name of the area that this view belongs to. */
    areaId: string;

    /** Id of the view. */
    viewId: string;

    /** Name of the View to load structure data from. */
    viewStructure: string;

    /** Name of the View to load document data from. */
    viewDocuments: string;

    /** Display order of the View. */
    order: number;

    extendedConfig: string | null;

    fieldSchema: string;

    fields: string;

    configurable: boolean;
}

/**
 * Create the redux slice.
 */
export const projectViewsSlice = createSlice({
    name: 'projectViews',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export const getCurrentView =
    ({ areaName, viewName }: { areaName: string; viewName: string }) =>
    (store: IAppReduxStore): IProjectViewData | null => {
        const views = store.projectViews.data;
        if (!views) return null;
        const viewKey =
            Object.keys(views).find(
                item => views[item].areaId === areaName && views[item].viewId === viewName,
            ) || '';
        return views[viewKey] ?? null;
    };

/**
 * Export slice reducer as default.
 */
export default projectViewsSlice.reducer;
